<template>
  <div class="databricks-dashboard" :aria-busy="isLoading ? 'true' : 'false'">
    <div
      v-if="isLoading"
      class="databricks-dashboard__loading"
      role="status"
      aria-live="polite"
    >
      <img src="@/assets/images/mceasy-loader.gif" width="80" alt="" />
      <span class="databricks-dashboard__loading-text">{{ $t('global.loading') }}</span>
    </div>

    <div v-if="hasError" class="databricks-dashboard__error" role="alert">
      <i class="icon-outline_alert-circle databricks-dashboard__error-icon" />
      <h5>{{ $t('dashboard.databricks.errorTitle') }}</h5>
      <p>{{ $t('dashboard.databricks.errorDescription') }}</p>
      <McButton
        variant="primary"
        :text="$t('dashboard.databricks.retry')"
        @click="initializeDashboard"
      />
    </div>

    <div
      v-show="!isLoading && !hasError"
      ref="dashboardContainer"
      class="databricks-dashboard__container"
    />
  </div>
</template>

<script>
import { DatabricksDashboard } from '@databricks/aibi-client'
import McButton from '@/components/McButton'
import { GET_DATABRICKS_DASHBOARD_EMBED_CONFIG } from '@/services/api/dashboard/databricks'

export default {
  components: {
    McButton,
  },
  data() {
    return {
      dashboard: null,
      isLoading: true,
      errorMessage: '',
      isComponentDestroyed: false,
    }
  },
  computed: {
    hasError() {
      return Boolean(this.errorMessage)
    },
  },
  mounted() {
    this.initializeDashboard()
  },
  beforeDestroy() {
    this.isComponentDestroyed = true
    this.destroyDashboard()
  },
  methods: {
    async fetchEmbedConfig() {
      const response = await this.$apollo.query({
        query: GET_DATABRICKS_DASHBOARD_EMBED_CONFIG,
        fetchPolicy: 'no-cache',
      })
      const payload = response?.data?.databricksDashboardEmbedConfig
      const config = payload?.databricksDashboardEmbedConfig

      if (
        !payload?.isSuccessful ||
        !config?.workspaceUrl ||
        !config?.workspaceId ||
        !config?.dashboardId ||
        !config?.embedToken
      ) {
        throw new Error('Databricks dashboard configuration is unavailable')
      }

      return config
    },
    async getNewToken() {
      const config = await this.fetchEmbedConfig()
      return config.embedToken
    },
    async initializeDashboard() {
      this.destroyDashboard()
      this.isLoading = true
      this.errorMessage = ''

      try {
        const config = await this.fetchEmbedConfig()
        if (this.isComponentDestroyed) return

        await this.$nextTick()
        this.dashboard = new DatabricksDashboard({
          instanceUrl: config.workspaceUrl,
          workspaceId: config.workspaceId,
          dashboardId: config.dashboardId,
          token: config.embedToken,
          container: this.$refs.dashboardContainer,
          getNewToken: this.getNewToken,
          config: {
            version: 1,
          },
        })
        this.dashboard.initialize()
        this.isLoading = false
      } catch (_error) {
        if (this.isComponentDestroyed) return
        this.isLoading = false
        this.errorMessage = 'DATABRICKS_DASHBOARD_UNAVAILABLE'
      }
    },
    destroyDashboard() {
      if (!this.dashboard) return
      this.dashboard.destroy()
      this.dashboard = null
    },
  },
}
</script>

<style lang="scss" scoped>
.databricks-dashboard {
  position: relative;
  width: 100%;
  height: calc(100vh - 220px);
  height: calc(100dvh - 220px);
  min-height: 480px;
  margin-bottom: 16px;
  overflow: hidden;
  background-color: $white;
  border: 1px solid $mep-color-text-100;
  border-radius: 8px;

  &__loading,
  &__error {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $white;
  }

  &__loading {
    flex-direction: column;
    gap: 12px;
  }

  &__loading-text {
    color: $mep-color-text-300;
    font-size: 14px;
    font-weight: 500;
  }

  &__error {
    flex-direction: column;
    padding: 32px;
    text-align: center;

    h5 {
      margin: 0 0 8px;
      color: $mep-color-text;
      font-size: 18px;
      font-weight: 600;
    }

    p {
      max-width: 520px;
      margin: 0 0 20px;
      color: $mep-color-text-300;
      line-height: 1.5;
    }
  }

  &__error-icon {
    width: 56px;
    height: 56px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    color: #d92d20;
    font-size: 28px;
    background-color: #fff1f0;
    border-radius: 50%;
  }

  &__container {
    width: 100%;
    height: 100%;
    background-color: $white;
  }
}

@media (max-width: 767.98px) {
  .databricks-dashboard {
    min-height: 420px;
    border-radius: 6px;

    &__error {
      padding: 24px 20px;
    }
  }
}
</style>
